import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Edit, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';
import PlanEntriesCard from './PlanEntriesCard';

const API = `${API_BASE_URL}/api`;

const ICS218Form = ({ currentExercise, theme }) => {
  const API = API_BASE_URL + '/api';
  const [databaseResources, setDatabaseResources] = useState([]); // Resources from database
  const [localEntries, setLocalEntries] = useState([]); // Local manual entries
  const [editingEntryId, setEditingEntryId] = useState(null); // Track which entry is being edited
  const [expandedTypes, setExpandedTypes] = useState({}); // Track which resource types are expanded
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [locations, setLocations] = useState([]); // Locations from map markers
  const [resourceFormErrors, setResourceFormErrors] = useState({});

  // Refs for ICS218 resource form required fields
  const ics218FieldRefs = {
    resource_type: useRef(null),
    identification: useRef(null),
  };
  
  // Fetch map markers as locations - filtered by current exercise
  const fetchMapMarkers = async () => {
    try {
      const exerciseId = currentExercise?.id;
      const url = exerciseId 
        ? `${API}/map-objects?type=marker&exercise_id=${exerciseId}`
        : `${API}/map-objects?type=marker`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setLocations(data.map(marker => ({ id: marker.id, name: marker.name })));
      }
    } catch (error) {
      console.error('Error fetching map markers:', error);
    }
  };

  // Fetch locations on component mount
  useEffect(() => {
    fetchMapMarkers();
  }, []);
  
  // Resource type options (same as Edit Resources form)
  const resourceTypes = [
    'Fire: Pumper',
    'Fire: Ladder',
    'Fire: Rescue',
    'Fire: Tanker',
    'Fire: Command',
    'Ambulance: Type 1 Box',
    'Ambulance: Type 2 Van',
    'Ambulance: Type 3 Box',
    'Ambulance: Rapid Response',
    'Ambulance: Multi Patient Transfer',
    'Ambulance: Bus',
    'Police: Car',
    'Police: Command',
    'Police: Tactical',
    'Emerg Response: Command Post',
    'Emerg Response: Communications',
    'Emerg Response: Utility',
    'Aircraft Fixed Wing: Transport',
    'Aircraft Fixed Wing: Med STARS',
    'Aircraft Fixed Wing: Search and Rescue',
    'Aircraft Fixed Wing: Water Bomber',
    'Aircraft Fixed Wing: Mil SAR',
    'Aircraft Fixed Wing: Mil Surveillance',
    'Aircraft Fixed Wing: Mil Transport',
    'Aircraft Helicopter: Water Bomber',
    'Aircraft Helicopter: Med STARS',
    'Aircraft Helicopter: Mil Heavy Lift',
    'Aircraft Helicopter: Mil Utility',
    'Aircraft Helicopter: Mil Search and Rescue',
    'Support Light/Duty: Pickup Truck',
    'Support Light/Duty: 4WD Vehicle',
    'Support Light/Duty: SUV',
    'Salvation: Food/Shelter Support',
    'Salvation: Mobile Kitchens',
    'Salvation: Response Unit',
    'Salvation: Weather/Food/Shelters',
    'Salvation: Shower Trailers',
    'Salvation: Multi-Purpose Support Vans',
    'Red Cross: Van/Bus/Triage',
    'Heavy/Special: Grater',
    'Heavy/Special: Dozer',
    'Heavy/Special: Snow Plow',
    'Heavy/Special: Backhoe',
    'Heavy/Special: Water Tender',
    'Heavy/Special: Auger',
    'Heavy/Special: Bucket',
    'Heavy/Special: Utility',
    'Heavy/Special: Crane',
    'Heavy/Special: Tow Truck',
    'Heavy/Special: Hydro/Power Utility',
    'Military: Transport',
    'Military: Auger',
    'Military: Utility',
    'Military: LAV support',
    'Support: Generator',
    'Support: Lighting Unit',
    'Other:'
  ];

  // Location options (same as Edit Resources form)
  const locationOptions = [
    'Command Post',
    'Hospital',
    'Fire Station',
    'Police Station',
    'Operations Center EOC',
    'Staging Area',
    'Medical Aid Station',
    'Medical',
    'Communications Center',
    'Municipal Water Department',
    'Emergency Equipment Warehouse',
    'Fire Hall',
    'Stationary Unit'
  ];
  
  // Resource form data for modal
  const [resourceFormData, setResourceFormData] = useState({
    resource_type: '',
    identification: '',
    order_request_no: '',
    incident_id: '',
    vehicle_equipment_make_model: '',
    features: '',
    vehicle_licence_id: '',
    exercise_start_date: '',
    incident_release_date: '',
    incident_release_time: '',
    description: '',
    work_assignment_special_inst: '',
    request: 0,
    quantity_available: 0,
    quantity_needed: 0,
    overhead_position: '',
    special_equip_supplies: '',
    location: '',
    requested_arr_datetime: '',
    status: 'Assigned',
    contact_person: '',
    contact_phone: '',
    involved_in_exercise: true,
    assigned_exercise: ''
  });


  // Load local entries from backend API
  useEffect(() => {
    if (!currentExercise?.id) return;
    const fetchLocalEntries = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/ics-218-entries/${currentExercise.id}`);
        setLocalEntries(response.data);
      } catch (error) {
        console.error('Error fetching ICS 218 entries:', error);
      }
    };
    fetchLocalEntries();
  }, [currentExercise?.id]);

  // Fetch database resources filtered by current exercise
  useEffect(() => {
    const fetchDatabaseResources = async () => {
      if (!currentExercise?.id) return;
      
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/resources`, { headers });
        const filteredResources = response.data.filter(r => 
          r.involved_in_exercise === true && 
          r.assigned_exercise === currentExercise.id
        );
        setDatabaseResources(filteredResources);
      } catch (error) {
        console.error('Error fetching resources:', error);
      }
    };
    
    fetchDatabaseResources();
  }, [currentExercise]);

  // Group resources by type
  const groupedResources = databaseResources.reduce((groups, resource) => {
    const type = resource.resource_type || 'Unspecified';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(resource);
    return groups;
  }, {});

  // Toggle resource type section
  const toggleType = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  // Open modal for adding new resource
  const handleAddResource = () => {
    setEditingResource(null);
    setIsEditMode(false);
    setResourceFormData({
      resource_type: '',
      identification: '',
      callsign: '',
      vehicle_icon: '',
      order_request_no: '',
      incident_id: '',
      vehicle_equipment_make_model: '',
      features: '',
      vehicle_licence_id: '',
      exercise_start_date: '',
      incident_release_date: '',
      description: '',
      work_assignment_special_inst: '',
      request: 0,
      quantity_available: 0,
      quantity_needed: 0,
      overhead_position: '',
      special_equip_supplies: '',
      location: '',
      requested_arr_datetime: '',
      status: 'Assigned',
      contact_person: '',
      contact_phone: '',
      involved_in_exercise: true,
      assigned_exercise: currentExercise?.id || ''
    });
    setShowResourceModal(true);
  };

  // Open modal for editing resource
  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setIsEditMode(true);
    setResourceFormData(resource);
    setShowResourceModal(true);
  };

  // Save resource (create or update)
  const handleSaveResource = async () => {
    // Validate required fields
    const newErrors = {};
    if (!resourceFormData.resource_type?.trim()) newErrors.resource_type = 'Resource type is required';
    if (!resourceFormData.identification?.trim()) newErrors.identification = 'Identification is required';
    setResourceFormErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const fieldOrder = ['resource_type', 'identification'];
      for (const field of fieldOrder) {
        if (newErrors[field] && ics218FieldRefs[field]?.current) {
          ics218FieldRefs[field].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const focusable = ics218FieldRefs[field].current.querySelector('input, textarea, button, select');
          if (focusable) setTimeout(() => focusable.focus(), 400);
          break;
        }
      }
      return;
    }

    try {
      const token = secureStorage.getToken();
      const headers = token ? { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      } : { 'Content-Type': 'application/json' };
      
      if (isEditMode && editingResource) {
        // Update existing resource
        await axios.put(`${API}/resources/${editingResource.id}`, resourceFormData, { headers });
      } else {
        // Create new resource
        await axios.post(`${API}/resources`, {
          ...resourceFormData,
          involved_in_exercise: true,
          assigned_exercise: currentExercise?.id || ''
        }, { headers });
      }
      
      // Refresh resources
      const response = await axios.get(`${API}/resources`, { headers });
      const filteredResources = response.data.filter(r => 
        r.involved_in_exercise === true && 
        r.assigned_exercise === currentExercise?.id
      );
      setDatabaseResources(filteredResources);
      setShowResourceModal(false);
      setIsEditMode(false);
      setEditingResource(null);
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Error saving resource. Please try again.');
    }
  };

  // Delete database resource
  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/resources/${resourceId}`, { headers });
      setDatabaseResources(databaseResources.filter(r => r.id !== resourceId));
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('Error deleting resource. Please try again.');
    }
  };

  // Local entry management
  const addLocalEntry = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/ics-218-entries/${currentExercise.id}`, {
        order_request_no: '',
        incident_id_no: '',
        resource_type: '',
        make_model: '',
        features: '',
        contact_person: '',
        vehicle_licence_id: '',
        work_assignment: '',
        exercise_start_date: '',
        exercise_start_time: '',
        incident_release_date: '',
        incident_release_time: ''
      });
      setLocalEntries(prev => [response.data, ...prev]);
      setEditingEntryId(response.data.id);
    } catch (error) {
      console.error('Error adding ICS 218 entry:', error);
    }
  };

  const updateLocalEntry = async (entryId, field, value) => {
    const entry = localEntries.find(e => e.id === entryId);
    if (!entry) return;
    const updated = { ...entry, [field]: value };
    setLocalEntries(prev => prev.map(e => e.id === entryId ? updated : e));
    try {
      await axios.put(`${API_BASE_URL}/api/ics-218-entries/${entryId}`, {
        order_request_no: updated.order_request_no || '',
        incident_id_no: updated.incident_id_no || '',
        resource_type: updated.resource_type || '',
        make_model: updated.make_model || '',
        features: updated.features || '',
        contact_person: updated.contact_person || '',
        vehicle_licence_id: updated.vehicle_licence_id || '',
        work_assignment: updated.work_assignment || '',
        exercise_start_date: updated.exercise_start_date || '',
        exercise_start_time: updated.exercise_start_time || '',
        incident_release_date: updated.incident_release_date || '',
        incident_release_time: updated.incident_release_time || ''
      });
    } catch (error) {
      console.error('Error updating ICS 218 entry:', error);
    }
  };

  const deleteLocalEntry = async (entryId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/ics-218-entries/${entryId}`);
      setLocalEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (error) {
      console.error('Error deleting ICS 218 entry:', error);
    }
  };

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>ICS 218 - Support Vehicle / Equipment Inventory</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .header h1 { margin: 0; font-size: 20px; }
            .header p { margin: 5px 0; font-size: 12px; }
            .section { margin: 20px 0; }
            .section-title { background: #333; color: white; padding: 8px; font-weight: bold; margin-top: 15px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th, td { border: 1px solid #000; padding: 6px; text-align: left; }
            th { background: #f0f0f0; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 10px; text-align: center; border-top: 1px solid #000; padding-top: 10px; }
            @media print {
              body { padding: 10px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 218 - SUPPORT VEHICLE / EQUIPMENT INVENTORY</h1>
            <p>Exercise: ${currentExercise?.name || currentExercise?.exercise_name || 'N/A'}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>

          ${Object.keys(groupedResources).length > 0 ? `
            <div class="section">
              <h2 style="font-size: 16px;">Database Resources (By Type)</h2>
              ${Object.entries(groupedResources).map(([type, resources]) => `
                <div class="section-title">${type} (${resources.length})</div>
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Make/Model</th>
                      <th>License/ID</th>
                      <th>Contact</th>
                      <th>Location</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${resources.map(r => `
                      <tr>
                        <td>${r.identification || '-'}</td>
                        <td>${r.vehicle_equipment_make_model || '-'}</td>
                        <td>${r.vehicle_licence_id || '-'}</td>
                        <td>${r.contact_person || '-'}</td>
                        <td>${r.location || '-'}</td>
                        <td>${r.status || '-'}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>
              `).join('')}
            </div>
          ` : ''}

          ${localEntries.length > 0 ? `
            <div class="section">
              <h2 style="font-size: 16px;">Local Manual Entries</h2>
              <table>
                <thead>
                  <tr>
                    <th>Order Req No.</th>
                    <th>Incident ID</th>
                    <th>Resource Type</th>
                    <th>Make/Model</th>
                    <th>Features</th>
                    <th>Contact</th>
                    <th>License/ID</th>
                  </tr>
                </thead>
                <tbody>
                  ${localEntries.map(entry => `
                    <tr>
                      <td>${entry.order_request_no || '-'}</td>
                      <td>${entry.incident_id_no || '-'}</td>
                      <td>${entry.resource_type || '-'}</td>
                      <td>${entry.make_model || '-'}</td>
                      <td>${entry.features || '-'}</td>
                      <td>${entry.contact_person || '-'}</td>
                      <td>${entry.vehicle_licence_id || '-'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}

          <div class="footer">
            ICS 218 - Support Vehicle / Equipment Inventory | EXRSIM Emergency Management Platform
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>ICS 218 - Support Vehicle / Equipment Inventory</h2>
          <p className={`${theme.colors.textMuted} mt-1`}>Manage vehicle and equipment resources for {currentExercise?.name || currentExercise?.exercise_name || 'this exercise'}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handlePrint} variant="outline" className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10">
            <Printer className="h-4 w-4 mr-2" />
            Print ICS 218
          </Button>
        </div>
      </div>

      {/* Local Manual Entries Section */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-orange-500">
            Local Manual Entries ({localEntries.length})
          </CardTitle>
          <Button onClick={addLocalEntry} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Local Entry
          </Button>
        </CardHeader>
        <CardContent>
        {localEntries.length === 0 ? (
          <div className="text-center py-8">
            <p className={`${theme.colors.textMuted}`}>No local entries yet.</p>
            <p className={`${theme.colors.textMuted} text-sm mt-2`}>
              Add manual entries for resources not in the database.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {localEntries.map((entry) => (
              <div key={entry.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
                {editingEntryId === entry.id ? (
                  /* ---- EDIT MODE ---- */
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-orange-500 font-semibold text-sm">
                        {(entry.resource_type?.split(': ').pop()) || 'New Entry'}{entry.make_model ? ` — ${entry.make_model}` : ''}
                      </h4>
                      <Button
                        size="sm"
                        onClick={() => setEditingEntryId(null)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        Done
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Order Request No.</Label>
                        <Input
                          type="text"
                          value={entry.order_request_no}
                          onChange={(e) => updateLocalEntry(entry.id, 'order_request_no', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Incident ID No.</Label>
                        <Input
                          type="text"
                          value={entry.incident_id_no}
                          onChange={(e) => updateLocalEntry(entry.id, 'incident_id_no', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Resource Type</Label>
                        <Select
                          value={entry.resource_type || ''}
                          onValueChange={(value) => updateLocalEntry(entry.id, 'resource_type', value)}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-700 border-gray-600">
                            {resourceTypes.map((type) => (
                              <SelectItem key={type} value={type} className="text-white hover:bg-gray-600">
                                {type}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Make / Model</Label>
                        <Input
                          type="text"
                          value={entry.make_model}
                          onChange={(e) => updateLocalEntry(entry.id, 'make_model', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Features</Label>
                        <Input
                          type="text"
                          value={entry.features}
                          onChange={(e) => updateLocalEntry(entry.id, 'features', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Contact Person</Label>
                        <Input
                          type="text"
                          value={entry.contact_person}
                          onChange={(e) => updateLocalEntry(entry.id, 'contact_person', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Vehicle Licence or ID #</Label>
                        <Input
                          type="text"
                          value={entry.vehicle_licence_id}
                          onChange={(e) => updateLocalEntry(entry.id, 'vehicle_licence_id', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Work Assignment</Label>
                        <Input
                          type="text"
                          value={entry.work_assignment}
                          onChange={(e) => updateLocalEntry(entry.id, 'work_assignment', e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Date/Time From</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={entry.exercise_start_date}
                            onChange={(e) => updateLocalEntry(entry.id, 'exercise_start_date', e.target.value)}
                            className="bg-gray-800 border-gray-600 text-white"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength={5}
                            value={entry.exercise_start_time || ''}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^\d:]/g, '');
                              if (v.length === 2 && !v.includes(':') && entry.exercise_start_time?.length < 3) v += ':';
                              updateLocalEntry(entry.id, 'exercise_start_time', v.slice(0, 5));
                            }}
                            className="bg-gray-800 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-white text-xs font-medium mb-1 block">Date/Time To</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={entry.incident_release_date}
                            onChange={(e) => updateLocalEntry(entry.id, 'incident_release_date', e.target.value)}
                            className="bg-gray-800 border-gray-600 text-white"
                          />
                          <Input
                            type="text"
                            placeholder="HH:MM"
                            maxLength={5}
                            value={entry.incident_release_time || ''}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^\d:]/g, '');
                              if (v.length === 2 && !v.includes(':') && entry.incident_release_time?.length < 3) v += ':';
                              updateLocalEntry(entry.id, 'incident_release_time', v.slice(0, 5));
                            }}
                            className="bg-gray-800 border-gray-600 text-white"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* ---- READ MODE ---- */
                  <>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-white font-semibold">
                        {(entry.resource_type?.split(': ').pop()) || 'Unspecified Type'}{entry.make_model ? ` — ${entry.make_model}` : ''}
                      </h4>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingEntryId(entry.id)}
                          className="text-blue-500 border-blue-500"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteLocalEntry(entry.id)}
                          className="text-red-500 border-red-500"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {entry.order_request_no && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Order Req No.</span>
                          <span className="text-white text-sm">{entry.order_request_no}</span>
                        </div>
                      )}
                      {entry.incident_id_no && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Incident ID</span>
                          <span className="text-white text-sm">{entry.incident_id_no}</span>
                        </div>
                      )}
                      {entry.features && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Features</span>
                          <span className="text-white text-sm">{entry.features}</span>
                        </div>
                      )}
                      {entry.contact_person && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Contact</span>
                          <span className="text-white text-sm">{entry.contact_person}</span>
                        </div>
                      )}
                      {entry.vehicle_licence_id && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Licence/ID</span>
                          <span className="text-white text-sm">{entry.vehicle_licence_id}</span>
                        </div>
                      )}
                      {entry.work_assignment && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Work Assignment</span>
                          <span className="text-white text-sm">{entry.work_assignment}</span>
                        </div>
                      )}
                      {(entry.exercise_start_date || entry.exercise_start_time) && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Date/Time From</span>
                          <span className="text-white text-sm">{entry.exercise_start_date}{entry.exercise_start_time ? ` ${entry.exercise_start_time}` : ''}</span>
                        </div>
                      )}
                      {(entry.incident_release_date || entry.incident_release_time) && (
                        <div>
                          <span className="text-orange-500 text-xs font-semibold block mb-1">Date/Time To</span>
                          <span className="text-white text-sm">{entry.incident_release_date}{entry.incident_release_time ? ` ${entry.incident_release_time}` : ''}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        </CardContent>
      </Card>

      {/* Plan Entries — time-bounded Title/From/To/Plan cards */}
      <PlanEntriesCard
        formType="ics_218"
        exerciseId={currentExercise?.id || ''}
        title="ICS 218 — Plan Entries"
        description="Time-bounded support-vehicle plan entries — each with a Title, From/To 24-hour date-times, and a free-text Plan"
      />

      {/* Resource Form */}
      {showResourceModal && (
          <Card className="border-gray-700">
            <CardHeader>
              <CardTitle className="text-orange-500">
                {isEditMode ? 'Edit Resource' : 'Add New Resource'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div ref={ics218FieldRefs.resource_type}>
                  <Label className="text-white text-xs font-medium mb-1 block">Resource Type <span className="text-red-500">*</span></Label>
                  <Select
                    value={resourceFormData.resource_type}
                    onValueChange={(value) => { setResourceFormData({...resourceFormData, resource_type: value}); setResourceFormErrors(prev => ({...prev, resource_type: ''})); }}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                      <SelectValue placeholder="Select resource type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600 z-[10001]">
                      {resourceTypes.map((type) => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-gray-700">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {resourceFormErrors.resource_type && (
                    <p className="text-red-400 text-sm mt-1">{resourceFormErrors.resource_type}</p>
                  )}
                </div>
                <div ref={ics218FieldRefs.identification}>
                  <Label className="text-white text-xs font-medium mb-1 block">Identification <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    value={resourceFormData.identification}
                    onChange={(e) => { setResourceFormData({...resourceFormData, identification: e.target.value}); setResourceFormErrors(prev => ({...prev, identification: ''})); }}
                    className="bg-gray-800 border-gray-600 text-white"
                    placeholder="e.g., Unit 23, Engine 5"
                  />
                  {resourceFormErrors.identification && (
                    <p className="text-red-400 text-sm mt-1">{resourceFormErrors.identification}</p>
                  )}
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Callsign</Label>
                  <Input
                    type="text"
                    value={resourceFormData.callsign}
                    onChange={(e) => setResourceFormData({...resourceFormData, callsign: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                    placeholder="e.g., Bandit 25, Engine 101"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Vehicle Icon</Label>
                  <Select
                    value={resourceFormData.vehicle_icon}
                    onValueChange={(val) => setResourceFormData({...resourceFormData, vehicle_icon: val})}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                      <SelectValue placeholder="Select icon for map..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      {[
                        { value: 'fire_truck', label: '\u{1F692} Fire Truck' },
                        { value: 'ambulance', label: '\u{1F691} Ambulance' },
                        { value: 'command', label: '\u{1F4E1} Command' },
                        { value: 'heavy_equipment', label: '\u{1F6DC} Heavy Equipment' },
                        { value: 'water_tender', label: '\u{1F4A7} Water Tender' },
                        { value: 'rescue', label: '\u26D1 Rescue' },
                        { value: 'hazmat', label: '\u2622 Hazmat' },
                        { value: 'helicopter', label: '\u{1F681} Helicopter' },
                        { value: 'boat', label: '\u26F5 Boat' },
                        { value: 'utility', label: '\u{1F527} Utility' },
                        { value: 'transport', label: '\u{1F68C} Transport' },
                        { value: 'police', label: '\u{1F6A8} Police' },
                      ].map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-gray-700">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Order Request No.</Label>
                  <Input
                    type="text"
                    value={resourceFormData.order_request_no}
                    onChange={(e) => setResourceFormData({...resourceFormData, order_request_no: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Incident ID</Label>
                  <Input
                    type="text"
                    value={resourceFormData.incident_id}
                    onChange={(e) => setResourceFormData({...resourceFormData, incident_id: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Make / Model</Label>
                  <Input
                    type="text"
                    value={resourceFormData.vehicle_equipment_make_model}
                    onChange={(e) => setResourceFormData({...resourceFormData, vehicle_equipment_make_model: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Features</Label>
                  <Input
                    type="text"
                    value={resourceFormData.features}
                    onChange={(e) => setResourceFormData({...resourceFormData, features: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Vehicle Licence or ID #</Label>
                  <Input
                    type="text"
                    value={resourceFormData.vehicle_licence_id}
                    onChange={(e) => setResourceFormData({...resourceFormData, vehicle_licence_id: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Contact Person</Label>
                  <Input
                    type="text"
                    value={resourceFormData.contact_person}
                    onChange={(e) => setResourceFormData({...resourceFormData, contact_person: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Contact Phone</Label>
                  <Input
                    type="text"
                    value={resourceFormData.contact_phone}
                    onChange={(e) => setResourceFormData({...resourceFormData, contact_phone: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Location</Label>
                  <Select
                    value={resourceFormData.location}
                    onValueChange={(value) => setResourceFormData({...resourceFormData, location: value})}
                  >
                    <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-700 border-gray-600 z-[10001]">
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.name} className="text-white hover:bg-gray-700">
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white text-xs font-medium mb-1 block">Status</Label>
                  <div className="mt-2 space-y-2">
                    {['Assigned', 'In Transit', 'Released', 'Unavailable'].map((statusOption, index) => {
                      const radioId = `modal-status-option-${index}`;
                      return (
                        <div key={statusOption} className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id={radioId}
                            name="modal-resource-status"
                            value={statusOption}
                            checked={resourceFormData.status === statusOption}
                            onChange={(e) => setResourceFormData({...resourceFormData, status: e.target.value})}
                            className="w-4 h-4 text-orange-500 bg-gray-800 border-gray-600 focus:ring-orange-500 focus:ring-2"
                          />
                          <Label 
                            htmlFor={radioId} 
                            className="text-white text-sm cursor-pointer"
                          >
                            {statusOption}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-xs font-medium mb-1 block">Date/Time From</Label>
                    <Input
                      type="date"
                      value={resourceFormData.exercise_start_date}
                      onChange={(e) => setResourceFormData({...resourceFormData, exercise_start_date: e.target.value})}
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs font-medium mb-1 block">Date/Time To</Label>
                    <Input
                      type="date"
                      value={resourceFormData.incident_release_date}
                      onChange={(e) => setResourceFormData({...resourceFormData, incident_release_date: e.target.value})}
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white text-xs font-medium mb-1 block">Description</Label>
                  <Textarea
                    value={resourceFormData.description}
                    onChange={(e) => setResourceFormData({...resourceFormData, description: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                    rows={2}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-white text-xs font-medium mb-1 block">Work Assignment / Special Instructions</Label>
                  <Textarea
                    value={resourceFormData.work_assignment_special_inst}
                    onChange={(e) => setResourceFormData({...resourceFormData, work_assignment_special_inst: e.target.value})}
                    className="bg-gray-800 border-gray-600 text-white"
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowResourceModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveResource} className="bg-green-600 hover:bg-green-700">
                  <Save className="h-4 w-4 mr-2" />
                  Save Resource
                </Button>
              </div>
            </CardContent>
          </Card>
      )}
    </div>
  );
};


export default ICS218Form;
